// The Subscribe page's one code field (#6984): what to do with a typed code, and the
// message for each answer, with no page state in them. Kept out of subscribe.ts,
// which would otherwise grow a whole state machine.
// See design-docs/subscribe-code-field.md.
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { ACCESS_KEY_LENGTH } from '@cf-shared/data-model/code-check-character';
import { TypedCodeShape, classifyTypedCode, normaliseTypedCode } from '@cf-shared/data-model/typed-code';
import { SubscribeRedeemOutcome } from '@cf-shared/subscribe-redeem-interface';

/** What pressing Apply does with the typed code. */
export enum CodeAction {
  /** A sale code: keep it and ask the server for the checkout again. */
  PromoPath = 'PromoPath',
  /** A friend's or access code typed while signed out: nothing is looked up; ask to sign in. */
  SignInFirst = 'SignInFirst',
  /** A friend's code, signed in: claim it. */
  Claim = 'Claim',
  /** An access code, signed in: confirm, then redeem. */
  ConfirmAccess = 'ConfirmAccess',
  /** A sale or friend code in the Android app, where only access codes work. */
  AppOnly = 'AppOnly',
  /** A near-miss or a code of a reserved length: answered on the device, with no call. */
  LocalMessage = 'LocalMessage',
}

export interface CodeDecision {
  action: CodeAction;
  /** The line to show, for every action that answers on the device. */
  message?: TranslatedString;
  /** For Claim and ConfirmAccess: the code to send, as typed but tidied. */
  code?: string;
}

/** The line for a shape the device answers itself. */
function localMessage(shape: TypedCodeShape): TranslatedString {
  switch (shape) {
    case TypedCodeShape.Mistyped:
      return LL.subscribe.code.mistyped();
    case TypedCodeShape.Incomplete:
      return LL.subscribe.code.incomplete();
    default:
      return LL.subscribe.code.notRecognised();
  }
}

/**
 * What Apply does with `typed`. `native` is the Android app (iOS shows no field at
 * all), where only an access code can be used: a sale or friend code there gets the
 * store's rule, and only a near-miss of an access code's length gets its typo line.
 */
export function decideCodeAction(typed: string, signedIn: boolean, native: boolean): CodeDecision {
  const { shape } = classifyTypedCode(typed);
  const code = normaliseTypedCode(typed);

  if (native) {
    if (shape === TypedCodeShape.Access) return { action: CodeAction.ConfirmAccess, code };
    const isNearAccessLength = Math.abs(code.length - ACCESS_KEY_LENGTH) <= 1;
    if (isNearAccessLength && (shape === TypedCodeShape.Mistyped || shape === TypedCodeShape.Incomplete)) {
      return { action: CodeAction.LocalMessage, message: localMessage(shape) };
    }
    return { action: CodeAction.AppOnly, message: LL.subscribe.code.appOnly() };
  }

  switch (shape) {
    case TypedCodeShape.Other:
      return { action: CodeAction.PromoPath, code: typed.trim() };
    case TypedCodeShape.Friend:
      return signedIn
        ? { action: CodeAction.Claim, code }
        : { action: CodeAction.SignInFirst, message: LL.subscribe.code.signedOutFriend(), code };
    case TypedCodeShape.Access:
      return signedIn
        ? { action: CodeAction.ConfirmAccess, code }
        : { action: CodeAction.SignInFirst, message: LL.subscribe.code.signedOutAccess(), code };
    default:
      return { action: CodeAction.LocalMessage, message: localMessage(shape) };
  }
}

/** The line under the field for each answer of `subscribeRedeemCode`. Empty for a success. */
export function outcomeMessage(outcome: SubscribeRedeemOutcome, notValidForDays?: number): TranslatedString | undefined {
  switch (outcome) {
    case SubscribeRedeemOutcome.FriendApplied:
    case SubscribeRedeemOutcome.AccessRedeemed:
      return undefined;
    case SubscribeRedeemOutcome.NotRecognised:
      return LL.subscribe.code.notRecognised();
    case SubscribeRedeemOutcome.NewAccountsOnly:
      return LL.subscribe.code.newAccountsOnly();
    case SubscribeRedeemOutcome.OwnCode:
      return LL.subscribe.code.ownCode();
    case SubscribeRedeemOutcome.AlreadyReferred:
      return LL.subscribe.code.alreadyReferred();
    case SubscribeRedeemOutcome.AlreadyUsed:
      return LL.subscribe.code.alreadyUsed();
    case SubscribeRedeemOutcome.AddsNothing:
      return LL.subscribe.code.addsNothing();
    case SubscribeRedeemOutcome.NotValidYet:
      // the server always sends the day count with this outcome; 1 is only a floor for a damaged answer
      return LL.subscribe.code.notValidYet({ days: notValidForDays ?? 1 });
    default:
      // TryAgain, and any outcome a newer server adds that this build does not know
      return LL.subscribe.code.tryAgain();
  }
}
